import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import FormBuilder from '../../../../../../../shared/components/form-builder/form-builder';
import { IAdditionalInfo, normalizeStringArray } from '../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto';
import { useFormActions } from '../../../../../../../shared/hooks/form-builder.hooks';
import { useTransactionCertificateStore } from '../../../../../../../store/transaction-certificates.store';
import useNotification from '../../../../../../../shared/hooks/useNotification';
import { buyerSellerDetailsService } from '../../../../tc-service/buyer-seller-details.service';
import { dtrackItService } from '../../../../tc-service/dtrackit.service';
import { formatRefCodeLabel } from '../../../../tc-service/ref-code-label.utils';
import {
  isFeedstockChipAllowed,
  isPackagingForChipAllowed,
  isPercentTextileWasteInRange,
  retainPercentTextileWasteInRange,
} from './additional-info.validation';

interface AdditionalInfoProps {
  formRef: React.MutableRefObject<any>;
  additionalInfoData: IAdditionalInfo;
  setAdditionalInfoData: React.Dispatch<React.SetStateAction<IAdditionalInfo>>;
  hasRecycledClaim?: boolean;
  productCategoryCode?: string;
  productDetailCode?: string;
  isWithComponent?: boolean;
}

interface AdditionalInfoFormValues extends IAdditionalInfo {
  tcProductOriginOrganizationTeIdSearch: string;
  tcProductOriginFacilityTeIdSearch: string;
}

interface TEDetail {
  TE_ID: string;
  [key: string]: any;
}

const disallowedPlaceholderValues = new Set([
  "na",
  "n/a",
  "not available",
  "null",
]);

const isBlankOrPlaceholder = (value: unknown) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return !normalized || disallowedPlaceholderValues.has(normalized);
};

const validOptionalText = (value: unknown) => {
  const normalized = String(value ?? "").trim();
  return !normalized || !isBlankOrPlaceholder(normalized);
};

const AdditionalInfo: React.FC<AdditionalInfoProps> = ({
  formRef,
  additionalInfoData,
  setAdditionalInfoData,
  productCategoryCode = "",
  productDetailCode = "",
  isWithComponent = false,
}) => {
  const { notify } = useNotification();
  const { updateField } = useFormActions("additional-Info");
  const { getTCStandardGOTS } = useTransactionCertificateStore();
  // Reclaimed Material Source is a multi-select of PR (process) ref codes per
  // DAT-501 row 60 (86ajey39k) — free-typed chips let invalid codes through.
  const [processCodeOptions, setProcessCodeOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    const fetchProcessCodes = async () => {
      try {
        const result: any = await dtrackItService.getReferenceCodes("PR");
        if (cancelled) return;
        const options = (result?.data || []).map((t: any) => ({
          value: t.code_1,
          label: formatRefCodeLabel(t.description_1, t.code_1),
        }));
        setProcessCodeOptions(options);
      } catch (error) {
        console.error("Error fetching PR process codes:", error);
      }
    };
    fetchProcessCodes();
    return () => {
      cancelled = true;
    };
  }, []);

  const initialValues: AdditionalInfoFormValues = {
    ...additionalInfoData,
    tcProductFeedstock: Array.isArray(additionalInfoData.tcProductFeedstock)
      ? additionalInfoData.tcProductFeedstock
      : additionalInfoData.tcProductFeedstock && String(additionalInfoData.tcProductFeedstock).trim()
        ? [String(additionalInfoData.tcProductFeedstock)]
        : [],
    // Normalize stored values to PR#### so they match the dropdown option values
    // (legacy rows may hold bare "0001" digits from the old chips input).
    tcProductReclaimedMaterialSource: (Array.isArray(additionalInfoData.tcProductReclaimedMaterialSource)
      ? additionalInfoData.tcProductReclaimedMaterialSource
      : additionalInfoData.tcProductReclaimedMaterialSource && String(additionalInfoData.tcProductReclaimedMaterialSource).trim()
        ? [String(additionalInfoData.tcProductReclaimedMaterialSource)]
        : []
    ).map((item) => {
      const raw = String(item).trim();
      return /^\d{4}$/.test(raw) ? `PR${raw}` : raw.toUpperCase();
    }),
    tcProductPackagingFor: normalizeStringArray(additionalInfoData.tcProductPackagingFor),
    tcProductOriginOrganizationTeId: Array.isArray(additionalInfoData.tcProductOriginOrganizationTeId)
      ? additionalInfoData.tcProductOriginOrganizationTeId
      : additionalInfoData.tcProductOriginOrganizationTeId
        ? [String(additionalInfoData.tcProductOriginOrganizationTeId)]
        : [],
    tcProductOriginFacilityTeId: Array.isArray(additionalInfoData.tcProductOriginFacilityTeId)
      ? additionalInfoData.tcProductOriginFacilityTeId
      : additionalInfoData.tcProductOriginFacilityTeId
        ? [String(additionalInfoData.tcProductOriginFacilityTeId)]
        : [],
    tcProductOriginOrganizationTeIdSearch: "",
    tcProductOriginFacilityTeIdSearch: "",
  };

  const fetchTEDetails = async (
    query: string,
    offset: number
  ): Promise<TEDetail[]> => {
    if (!query || query.length < 3) {
      return [];
    }
    try {
      const queryParams: Record<string, string | number> = {
        query_param: query,
        offset,
        name: "",
      };
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      return response?.data || [];
    } catch (error) {
      console.error("Error fetching TE details:", error);
      notify("Failed to fetch TE details", "error");
      return [];
    }
  };
  const formConfig = {
    pageName: "additional-Info",
    subPageName: "tcProductAdditionalInfo",
    initialValues,
    validationSchema: Yup.object().shape({
      tcProductOriginOrganizationTeId: Yup.array()
        .of(Yup.string()
          .matches(/^TE-\d{8}$/, "Origin Organization TE-ID must use format TE-########")
          .max(11, "Each TE-ID must be at most 11 characters"))
        .min(1, "Product Origin Organization TE-ID is required")
        .required("Product Origin Organization TE-ID is required"),
      tcProductOriginFacilityTeId: Yup.array()
        .of(Yup.string()
          .matches(/^TE-\d{8}$/, "Origin Facility TE-ID must use format TE-########")
          .max(11, "Each TE-ID must be at most 11 characters"))
        .min(1, "Product Origin Facility TE-ID is required")
        .required("Product Origin Facility TE-ID is required"),
      tcProductOriginOrganizationTeIdSearch: Yup.string().nullable(),
      tcProductOriginFacilityTeIdSearch: Yup.string().nullable(),
      tcProductInfoFiberLength: Yup.string()
        .when([], {
          is: () => {
            const fiberCategories = ["PC0032", "PC0033", "PC0034"];
            return getTCStandardGOTS || fiberCategories.includes(productCategoryCode);
          },
          then: Yup.string()
            .required("Fiber Length is required")
            .test(
              "not-blank-or-placeholder",
              "Fiber Length cannot be blank, na, n/a, not available, or null",
              (value: string | undefined) => !isBlankOrPlaceholder(value)
            ),
          otherwise: Yup.string()
            .nullable()
            .test(
              "not-placeholder-when-provided",
              "Fiber Length cannot be na, n/a, not available, or null",
              (value: string | null | undefined) => validOptionalText(value)
            ),
        })
        .max(255, "Maximum 255 characters allowed"),
      tcProductInfoYarnCount: Yup.string()
        .when([], {
          is: () => {
            const yarnCategories = ["PC0029", "PC0030", "PC0031"];
            return yarnCategories.includes(productCategoryCode);
          },
          then: Yup.string()
            .required("Yarn Count is required")
            .test(
              "not-blank-or-placeholder",
              "Yarn Count cannot be blank, na, n/a, not available, or null",
              (value: string | undefined) => !isBlankOrPlaceholder(value)
            ),
          otherwise: Yup.string()
            .nullable(),
        })
        .max(255, "Maximum 255 characters allowed"),
      tcProductInfoFabricWeightAndConstruct: Yup.string()
        .when([], {
          is: () => {
            if (isWithComponent) return false;
            const fabricCategories = ["PC0025", "PC0026", "PC0027", "PC0028", "PC0039"];
            return fabricCategories.includes(productCategoryCode);
          },
          then: Yup.string()
            .required("Fabric Weight and Construct is required")
            .test(
              "not-blank-or-placeholder",
              "Fabric Weight and Construct cannot be blank, na, n/a, not available, or null",
              (value: string | undefined) => !isBlankOrPlaceholder(value)
            ),
          otherwise: Yup.string()
            .nullable(),
        })
        .max(255, "Maximum 255 characters allowed"),
      tcProductInfoFabricWeightOfFinishedProduct: Yup.string()
        .when([], {
          is: () => {
            if (isWithComponent) return false;
            const productCategories = [
              "PC0001", "PC0002", "PC0003", "PC0004", "PC0005", "PC0006",
              "PC0007", "PC0008", "PC0009", "PC0010", "PC0015", "PC0018",
              "PC0019", "PC0021",
            ];
            const productDetails = [
              "PD0001", "PD0002", "PD0003", "PD0004", "PD0005", "PD0006",
              "PD0007", "PD0008", "PD0009", "PD0010", "PD0011", "PD0012",
              "PD0013", "PD0014", "PD0015", "PD0016", "PD0017", "PD0019",
              "PD0020", "PD0021", "PD0022", "PD0023", "PD0024", "PD0026",
              "PD0028", "PD0029", "PD0042", "PD0043", "PD0044", "PD0051",
            ];
            return productCategories.includes(productCategoryCode) && productDetails.includes(productDetailCode);
          },
          then: Yup.string()
            .required("Fabric Weight of Finished Product is required")
            .test(
              "not-blank-or-placeholder",
              "Fabric Weight of Finished Product cannot be blank, na, n/a, not available, or null",
              (value: string | undefined) => !isBlankOrPlaceholder(value)
            ),
          otherwise: Yup.string()
            .nullable()
            .test(
              "not-placeholder-when-provided",
              "Fabric Weight of Finished Product cannot be na, n/a, not available, or null",
              (value: string | null | undefined) => validOptionalText(value)
            ),
        })
        .max(255, "Maximum 255 characters allowed"),
      tcProductGtin: Yup.string()
        .nullable()
        .max(255, "Maximum 255 characters allowed"),
      tcProductInfoAdditional: Yup.string()
        .nullable()
        .max(255, "Maximum 255 characters allowed"),
      tcProductInfoTechnicalSpecifications: Yup.string()
        .nullable()
        .max(255, "Maximum 255 characters allowed"),
      // DAT-501 "tc Product Feedstock": each value must match PC####-PD####-RM####;
      // the field itself is mandatory only when an incoming TC is an RMDF, which is
      // enforced at final submit (incoming TCs are linked after the product step).
      tcProductFeedstock: Yup.array()
        .of(Yup.string()
          .test(
            "is-valid-feedstock-code",
            "ProductFeedstock : Invalid value. Expected format PC####-PD####-RM#### (e.g. PC0023-PD0059-RM0104)",
            (value: string | null | undefined) => {
              if (!value) return true;
              return /^PC\d{4}-PD\d{4}-RM\d{4}$/i.test(String(value).trim());
            }
          ))
        .nullable(),
      tcProductPercentTextileWaste: Yup.string()
        .nullable()
        .test(
          "is-max-five-decimal-places",
          "ProductPercentTextileWaste : Must be a valid decimal number with up to 5 decimal places",
          (value: string | null | undefined) => {
            if (!value) return true;
            return /^(0|[1-9]\d*|0\.\d{1,5}|[1-9]\d*\.\d{1,5})$/.test(value);
          }
        )
        .test(
          "is-between-zero-and-one-hundred",
          "Product Percent Textile Waste must be between 0 and 100",
          isPercentTextileWasteInRange
        )
        .max(255, "Maximum 255 characters allowed"),
      tcProductPackagingFor: Yup.array()
        .of(
          Yup.string()
            .test(
              "not-blank-or-placeholder",
              "Product Packaging For cannot be blank, na, n/a, not available, or null",
              (value: string | undefined) => !isBlankOrPlaceholder(value)
            )
            .max(100, "Each product packaging for entry must be at most 100 characters")
        )
        .nullable(),
      // DAT-501 "tc Product Reclaimed Material Source": values are PR (process)
      // codes — matches the submit validator (PRs 7884/7885/7886);
      // mandatory only when an incoming TC is an RMDF (enforced at final submit).
      tcProductReclaimedMaterialSource: Yup.array()
        .of(Yup.string()
          .test(
            "is-valid-pr-code",
            "ProductReclaimedMaterialSource : Invalid code. Expected format PR followed by 4 digits (e.g. PR0001) or enter 4 digits and we will prefix 'PR' automatically",
            (value: string | null | undefined) => {
              if (!value) return true;
              const v = String(value).trim();
              const prPattern = /^PR\d{4}$/i;
              const digitsPattern = /^\d{4}$/;
              return prPattern.test(v) || digitsPattern.test(v);
            }
          )
          .max(255, "Each reclaimed material source must be at most 255 characters")
        )
        .nullable(),
    }),
    sections: [
      {
        title: 'Additional Information',
        // Field order/positions mirror the "Tc v4 Product wo Comp Wireframe 1" slide 8 layout:
        // two 4-up rows of text fields, then half-width chip blocks (Origin Org | Origin
        // Facility, Feedstock | Reclaimed Source, Packaging For).
        fields: [
          {
            label: 'Product Gtin',
            name: 'tcProductGtin',
            type: 'text',
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            props: {
              placeholder: 'Enter Product Gtin',
            },
          },
          {
            label: 'Product Percent Textile Waste',
            name: 'tcProductPercentTextileWaste',
            type: 'number',
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            props: {
              type: 'number',
              inputProps: {
                min: 0,
                max: 100,
                step: 'any',
              },
              placeholder: 'Enter Product Percent Waste',
            },
            events: {
              onChange: (value: string, values: AdditionalInfoFormValues, setFieldValue: any) => {
                const retainedValue = retainPercentTextileWasteInRange(
                  value,
                  values.tcProductPercentTextileWaste
                );
                if (retainedValue !== value) {
                  setFieldValue('tcProductPercentTextileWaste', retainedValue);
                }
              },
            },
          },
          {
            label: 'Product Info Technical Specifications',
            name: 'tcProductInfoTechnicalSpecifications',
            type: 'text',
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            props: {
              placeholder: 'Enter Product Technical Specifications',
            },
          },
          {
            label: 'Product Additional Info',
            name: 'tcProductInfoAdditional',
            type: 'text',
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            props: {
              placeholder: 'Enter Additional Info',
            },
          },
          {
            label: 'Product Info Fiber Length (mm)',
            name: 'tcProductInfoFiberLength',
            type: 'text',
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: (() => {
              const fiberCategories = ["PC0032", "PC0033", "PC0034"];
              return getTCStandardGOTS || fiberCategories.includes(productCategoryCode);
            })(),
            props: {
              placeholder: 'Enter Fiber Length',
            },
          },
          {
            label: 'Product Info Yarn Count',
            name: 'tcProductInfoYarnCount',
            type: 'text',
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: (() => {
              const yarnCategories = ["PC0029", "PC0030", "PC0031"];
              return yarnCategories.includes(productCategoryCode);
            })(),
            props: {
              placeholder: 'Enter Yarn Count',
            },
          },
          {
            label: 'Product Info Fabric Weight and Construct(kg)',
            name: 'tcProductInfoFabricWeightAndConstruct',
            type: 'text',
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: (() => {
              if (isWithComponent) return false;
              const fabricCategories = ["PC0025", "PC0026", "PC0027", "PC0028", "PC0039"];
              return fabricCategories.includes(productCategoryCode);
            })(),
            props: {
              placeholder: 'Enter Fabric Weight and Construct',
            },
          },
          {
            label: 'Product Info Fabric Weight of Finished Product (kg)',
            name: 'tcProductInfoFabricWeightOfFinishedProduct',
            type: 'text',
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: (() => {
              if (isWithComponent) return false;
              const productCategories = [
                "PC0001", "PC0002", "PC0003", "PC0004", "PC0005", "PC0006",
                "PC0007", "PC0008", "PC0009", "PC0010", "PC0015", "PC0018",
                "PC0019", "PC0021",
              ];
              const productDetails = [
                "PD0001", "PD0002", "PD0003", "PD0004", "PD0005", "PD0006",
                "PD0007", "PD0008", "PD0009", "PD0010", "PD0011", "PD0012",
                "PD0013", "PD0014", "PD0015", "PD0016", "PD0017", "PD0019",
                "PD0020", "PD0021", "PD0022", "PD0023", "PD0024", "PD0026",
                "PD0028", "PD0029", "PD0042", "PD0043", "PD0044", "PD0051",
              ];
              return productCategories.includes(productCategoryCode) && productDetails.includes(productDetailCode);
            })(),
            props: {
              placeholder: 'Enter Fabric Weight of Finished Product',
            },
          },
          // Origin TE-IDs: the chips block only DISPLAYS the selected IDs — the single
          // search input below is the one entry point (search a TE-ID, or type
          // TE-99999999 and press Enter). Two side-by-side input boxes read as a
          // duplicate field (86ajey364).
          {
            label: 'Product Origin Organization TE-ID',
            name: 'tcProductOriginOrganizationTeId',
            type: 'chips',
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            props: {
              disableManualEntry: true,
            },
          },
          {
            label: 'Product Origin Facility TE-ID',
            name: 'tcProductOriginFacilityTeId',
            type: 'chips',
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            props: {
              disableManualEntry: true,
            },
          },
          {
            label: '',
            name: 'tcProductOriginOrganizationTeIdSearch',
            type: 'search',
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            events: {
              onChange: (
                selected: TEDetail | null,
                values: AdditionalInfoFormValues,
                setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
              ) => {
                if (selected && selected.TE_ID) {
                  const existing = Array.isArray(values.tcProductOriginOrganizationTeId)
                    ? values.tcProductOriginOrganizationTeId
                    : values.tcProductOriginOrganizationTeId
                      ? [String(values.tcProductOriginOrganizationTeId)]
                      : [];
                  if (!existing.includes(selected.TE_ID)) {
                    setFieldValue("tcProductOriginOrganizationTeId", [
                      ...existing,
                      selected.TE_ID,
                    ]);
                  }
                }
                values.tcProductOriginOrganizationTeIdSearch = "";
                setFieldValue("tcProductOriginOrganizationTeIdSearch", "", false);
              },
            },
            props: {
              pageName: "Product Origin Organization TE-ID",
              placeholder: "Search TE-ID, or type 99999999 and press Enter",
              displayKey: "TE_ID",
              restrictInputFormat: true,
              clearInputOnSelect: true,
              commitInputPattern: /^TE-\d{8}$/i,
              onSearch: fetchTEDetails,
            },
          },
          {
            label: '',
            name: 'tcProductOriginFacilityTeIdSearch',
            type: 'search',
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            events: {
              onChange: (
                selected: TEDetail | null,
                values: AdditionalInfoFormValues,
                setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
              ) => {
                if (selected && selected.TE_ID) {
                  const existing = Array.isArray(values.tcProductOriginFacilityTeId)
                    ? values.tcProductOriginFacilityTeId
                    : values.tcProductOriginFacilityTeId
                      ? [String(values.tcProductOriginFacilityTeId)]
                      : [];
                  if (!existing.includes(selected.TE_ID)) {
                    setFieldValue("tcProductOriginFacilityTeId", [
                      ...existing,
                      selected.TE_ID,
                    ]);
                  }
                }
                values.tcProductOriginFacilityTeIdSearch = "";
                setFieldValue("tcProductOriginFacilityTeIdSearch", "", false);
              },
            },
            props: {
              pageName: "Product Origin Facility TE-ID",
              placeholder: "Search TE-ID, or type 99999999 and press Enter",
              displayKey: "TE_ID",
              restrictInputFormat: true,
              clearInputOnSelect: true,
              commitInputPattern: /^TE-\d{8}$/i,
              onSearch: fetchTEDetails,
            },
          },
          {
            label: "Product Feedstock",
            name: 'tcProductFeedstock',
            type: 'chips',
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            helperText: '(Format: PC####-PD####-RM####)',
            props: {
              placeholder: 'Enter codes like PC0023-PD0059-RM0104 (required for RMDF inputs)',
              isChipAllowed: isFeedstockChipAllowed,
              invalidChipMessage: 'Feedstock must match PC####-PD####-RM####',
            },
          },
          {
            label: "Product Material Reclaimed Source",
            name: 'tcProductReclaimedMaterialSource',
            // DAT-501 row 60: multi-select dropdown of PR codes from ref data
            // (86ajey39k) — free-typed chips produced invalid-code errors (86ajey3aj).
            type: 'select',
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: {
              multiple: true,
              enableSearch: true,
              options: processCodeOptions,
              placeholder: "Select reclaimed material source (required for RMDF inputs)",
            },
          },
          {
            label: 'Product Packaging For',
            name: 'tcProductPackagingFor',
            type: 'chips',
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: {
              placeholder: 'Enter Product Packaging For',
              isChipAllowed: isPackagingForChipAllowed,
              invalidChipMessage: 'Product Packaging For cannot be blank, na, n/a, not available, or null',
            },
          },
        ],
      },
    ],
    handleSubmit: async (values: AdditionalInfoFormValues) => {
      try {

        const { tcProductOriginOrganizationTeIdSearch, tcProductOriginFacilityTeIdSearch, ...rest } = values;

        // Normalize reclaimed material source array to the expected PR#### format
        const normalized = { ...rest } as any;

        if (Array.isArray(normalized.tcProductReclaimedMaterialSource)) {
          normalized.tcProductReclaimedMaterialSource = normalized.tcProductReclaimedMaterialSource.map((item: string) => {
            const raw = String(item).trim();
            const digitsPattern = /^\d{4}$/;
            const prPattern = /^PR(\d{4})$/i;
            if (digitsPattern.test(raw)) {
              return `PR${raw}`;
            } else if (prPattern.test(raw)) {
              const m = raw.match(prPattern);
              return `PR${m ? m[1] : raw.slice(2)}`;
            } else {
              return raw;
            }
          });
        }

        // Ensure feedstock is always an array of uppercase PC####-PD####-RM#### codes
        if (!Array.isArray(normalized.tcProductFeedstock)) {
          normalized.tcProductFeedstock = normalized.tcProductFeedstock && String(normalized.tcProductFeedstock).trim() ? [String(normalized.tcProductFeedstock)] : [];
        }
        normalized.tcProductFeedstock = normalized.tcProductFeedstock.map((item: string) =>
          String(item).trim().toUpperCase()
        );
        normalized.tcProductPackagingFor = normalizeStringArray(normalized.tcProductPackagingFor);

        const sanitizedData: IAdditionalInfo = {
          ...normalized,
          tcProductOriginOrganizationTeId: Array.isArray(normalized.tcProductOriginOrganizationTeId)
            ? Array.from(new Set(normalized.tcProductOriginOrganizationTeId))
            : [],
          tcProductOriginFacilityTeId: Array.isArray(normalized.tcProductOriginFacilityTeId)
            ? Array.from(new Set(normalized.tcProductOriginFacilityTeId))
            : [],
        };

        setAdditionalInfoData(sanitizedData);

        return sanitizedData;
      } catch (error) {
        console.error("Error saving additional info:", error);
        throw error;
      }
    },
  };
  

  return <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />;
};

export default AdditionalInfo;
